import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/auth";
import Seo from "../../components/Seo";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [questions, setQuestions] = useState({ q1: "", q2: "" });
  const [answers, setAnswers] = useState({ a1: "", a2: "" });
  const [newPass, setNewPass] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  const start = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/reset/start", { email });
      if (!data.ok) return setErr(data.error || "We couldn't find that account.");
      setQuestions({ q1: data.security_q1, q2: data.security_q2 });
      setStep(2);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const finish = async (e) => {
    e.preventDefault();
    setErr("");
    if (newPass.length < 8) return setErr("Password must be at least 8 characters.");
    if (newPass !== confirm) return setErr("Passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/reset/verify", {
        email, answer_1: answers.a1, answer_2: answers.a2, new_password: newPass,
      });
      setDone(true);
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main id="main-content" className="bg-[var(--ssc-paper)] min-h-[70vh] py-12">
      <Seo title="Reset Password | Scott Sells Chevys" description="Reset your password using your security questions." />
      <div className="max-w-md mx-auto px-4">
        <div className="ssc-card">
          <div className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-[var(--ssc-blue)]">Account Recovery</div>
          <h1 className="text-3xl font-black text-[var(--ssc-navy)] mt-1">Reset password</h1>

          {done ? (
            <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm" data-testid="reset-done">
              Password updated. Redirecting to sign-in…
            </div>
          ) : step === 1 ? (
            <form onSubmit={start} className="mt-5 space-y-3" data-testid="reset-step1">
              <p className="text-[var(--ssc-ink-2)] text-sm">Enter your account email and we'll show your security questions.</p>
              <div>
                <label className="ssc-label">Email</label>
                <input data-testid="reset-email" type="email" className="ssc-input" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              {err && <div className="text-sm text-[var(--ssc-red)]" data-testid="reset-error1">{err}</div>}
              <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full" data-testid="reset-next">
                {busy ? "Looking up…" : "Continue"}
              </button>
            </form>
          ) : (
            <form onSubmit={finish} className="mt-5 space-y-3" data-testid="reset-step2">
              <div>
                <label className="ssc-label">{questions.q1}</label>
                <input data-testid="reset-a1" className="ssc-input" value={answers.a1} onChange={(e) => setAnswers({ ...answers, a1: e.target.value })} required />
              </div>
              <div>
                <label className="ssc-label">{questions.q2}</label>
                <input data-testid="reset-a2" className="ssc-input" value={answers.a2} onChange={(e) => setAnswers({ ...answers, a2: e.target.value })} required />
              </div>
              <div>
                <label className="ssc-label">New password</label>
                <input data-testid="reset-newpass" type="password" className="ssc-input" value={newPass} onChange={(e) => setNewPass(e.target.value)} required minLength={8} />
              </div>
              <div>
                <label className="ssc-label">Confirm new password</label>
                <input data-testid="reset-confirm" type="password" className="ssc-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} />
              </div>
              {err && <div className="text-sm text-[var(--ssc-red)]" data-testid="reset-error2">{err}</div>}
              <button type="submit" disabled={busy} className="ssc-btn ssc-btn-primary w-full" data-testid="reset-submit">
                {busy ? "Saving…" : "Reset Password"}
              </button>
            </form>
          )}

          <div className="mt-4 text-sm">
            <Link to="/login" className="text-[var(--ssc-blue)] font-semibold">← Back to sign-in</Link>
          </div>
        </div>
      </div>
    </main>
  );
}
